import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { AccountStructureEnum } from '@app/enums/account-structure.enum';
import { AccountStructure } from '@app/models/fund-management';
import { AccountStructureFund, AccountStructureSite, AccountType, AccountTypes, UsesNcNewAcctStructure } from '../models/account-structure';

@Injectable()
export class AccountStructureService {
	endpointBase = '/api/account-structure';

	constructor(private readonly http: HttpClient) {}

	/**
	 * Pass in none, one or more AccountStructureEnum
	 * getStructure() => { accountStructure: { .... }}
	 * getStructure(ExpenseAccountMask) => '0.0000.000.000.000.000.00'
	 * getStructure([ExpenseAccountMask, LiabilityAccountMask] => ['0.0000.000.000.000.000.00', '0.0000.000.000.000.000.00']
	 */
	public getStructure(structure?: AccountStructureEnum | AccountStructureEnum[]): Observable<any> {
		return this.http.get(this.endpointBase).pipe(
			map((response: any) => {
				if (!structure) {
					return response;
				}
				if (Array.isArray(structure)) {
					return structure.map((key) => response.accountStructure?.[key]);
				}
				return response.accountStructure?.[structure];
			}),
			shareReplay(1),
		);
	}

	/**
	 * This method is only intended to be used to retrieve the account structure for use in account element range table
	 * filtering. For NC units using the new DPI account structure, the grant year is split out into a separate element.
	 */
	public getStructureWithGrantYear(): Observable<any> {
		return this.http.get(this.endpointBase + '/with-grant-year');
	}

	getAccountTypes() {
		return this.http.get<AccountTypes>(this.endpointBase + '/types');
	}

	getFunds() {
		return this.http.get<AccountStructureFund>(this.endpointBase + '/funds');
	}

	getSites() {
		return this.http.get<AccountStructureSite>(this.endpointBase + '/sites');
	}

	getFundsLessGrantYear(fundsLessGrantYear: boolean) {
		return this.http.get<AccountStructureFund>(this.endpointBase + '/funds-less-grant-year', {
			params: {
				fundsLessGrantYear,
			},
		});
	}

	usesNcNewAcctStructure(): Observable<UsesNcNewAcctStructure> {
		return this.http.get<UsesNcNewAcctStructure>(this.endpointBase + '/uses-nc-new-structure');
	}

	factorizeAccountStructure(accountStructure: AccountStructure, removeFund: boolean): string[] {
		let temp = [];
		if (accountStructure.UnifiedElementMap !== null) {
			temp = accountStructure.UnifiedElementMap.split('.');
			if (removeFund) {
				temp.splice(accountStructure.UnifiedElementFundIndex, 1);
			}
		} else {
			const assetElements = accountStructure.AssetElementMap.split('.');
			const liabilityElements = accountStructure.LiabilityElementMap.split('.');
			const reserveElements = accountStructure.ReserveElementMap.split('.');
			const revenueElements = accountStructure.RevenueElementMap.split('.');
			const expenseElements = accountStructure.ExpenseElementMap.split('.');
			if (removeFund) {
				assetElements.splice(accountStructure.AssetElementFundIndex, 1);
				liabilityElements.splice(accountStructure.LiabilityElementFundIndex, 1);
				reserveElements.splice(accountStructure.ReserveElementFundIndex, 1);
				revenueElements.splice(accountStructure.RevenueElementFundIndex, 1);
				expenseElements.splice(accountStructure.ExpenseElementFundIndex, 1);
			}
			temp = _.union(assetElements, liabilityElements, reserveElements, revenueElements, expenseElements);
		}
		return temp;
	}

	/**
	 * Prepares account type options from a list of account structures as returned by the getAccountTypes method.
	 * Additional properties such as elementMap and accountMask are included for convenience.
	 * @param accountStructures
	 * @returns
	 */
	public getAccountTypeOptions(accountStructures: AccountType[]): any[] {
		const options = accountStructures.map((accountStructure) => {
			return {
				value: accountStructure.Id,
				label: accountStructure.Description,
				elementMap: accountStructure.ElementMap,
				accountMask: accountStructure.Mask,
				questionMarkMask: this.getQuestionMarkMask(accountStructure.Mask),
			};
		});
		return options;
	}

	/**
	 * Generates a question mark mask from the given account mask.
	 * @param accountMask
	 * @returns
	 */
	public getQuestionMarkMask(accountMask: string): string {
		if (!accountMask || accountMask.length === 0) {
			return '';
		}
		return accountMask.replace(/0/g, '?');
	}

	/**
	 * Retrieves the default question mark mask for the given account structure.
	 * It uses the UnifiedAccountMask if available, otherwise falls back to the ExpenseAccountMask.
	 * @param accountStructure
	 * @returns
	 */
	public getDefaultQuestionMarkMask(accountStructure: AccountStructure): string {
		const accountMask = accountStructure.UnifiedAccountMask || accountStructure.ExpenseAccountMask;
		return this.getQuestionMarkMask(accountMask);
	}
}
